import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Package, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { useDeliveries } from '../context/DeliveryContext';
import { useAuth } from '../context/AuthContext';
import { getRoleConfig } from '../config/roles';
import { exportOrdersCsv } from '../utils/csvExport';
import { DATE_RANGE_OPTIONS } from '../services/deliveryService';
import Header from './layout/Header';
import SearchBar from './ui/SearchBar';
import FilterTabs from './ui/FilterTabs';
import StatsCards from './ui/StatsCards';
import DateRangeFilter from './ui/DateRangeFilter';
import DeliveryCard from './DeliveryCard';
import AddNoteModal from './modals/AddNoteModal';
import CompleteConfirmModal from './modals/CompleteConfirmModal';
import SetSoNumberModal from './modals/SetSoNumberModal';
import LoadingSpinner from './ui/LoadingSpinner';
import SectionHeader from './ui/SectionHeader';

export default function Dashboard() {
  const {
    deliveries,
    loading,
    error,
    statusCounts,
    operationType,
    loadDeliveries,
    changeOperationType,
    markAsActioned,
    addNote,
    splitDeliveries,
    submitSoNumber,
  } = useDeliveries();

  const { user } = useAuth();
  const roleConfig = getRoleConfig(user?.role);

  const [searchTerm, setSearchTerm] = useState('');
  const [daysBack, setDaysBack] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [noteModal, setNoteModal] = useState({ isOpen: false, delivery: null });
  const [completeModal, setCompleteModal] = useState({ isOpen: false, delivery: null });
  const [soNumberModal, setSoNumberModal] = useState({ isOpen: false, delivery: null });
  const [isProcessing, setIsProcessing] = useState(false);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const lastAppliedKey = useRef(null);
  const prevLoading = useRef(false);
  const filterKey = `${operationType}|${daysBack}|${searchTerm}`;

  // Load deliveries when operation type or date range changes
  useEffect(() => {
    loadDeliveries({ operation: operationType, search: searchTerm, daysBack });
  }, [operationType, daysBack, loadDeliveries]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadDeliveries({ operation: operationType, search: searchTerm, daysBack });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, operationType, daysBack, loadDeliveries]);

  const handleOperationChange = (op) => {
    changeOperationType(op);
    setSearchTerm('');
    setExpandedId(null);
  };

  const { needsAction, completed } = useMemo(
    () => splitDeliveries(deliveries, operationType),
    [deliveries, operationType, splitDeliveries]
  );

  useEffect(() => {
    // Reset on the loading falling edge (true -> false), not on a stale
    // pre-fetch `loading=false`. Effects fire in declaration order, so on a
    // filter change the load effect's `setLoading(true)` is queued for the
    // next render — without `prevLoading` this effect would otherwise apply
    // the default using stale `needsAction` and then block the legitimate
    // post-fetch reset via `lastAppliedKey`.
    if (prevLoading.current && !loading && lastAppliedKey.current !== filterKey) {
      setSelectedIds(new Set(needsAction.map((d) => d.id)));
      lastAppliedKey.current = filterKey;
    }
    prevLoading.current = loading;
  }, [filterKey, loading, needsAction]);

  const handleToggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSection = (rows, shouldSelect) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const r of rows) {
        if (shouldSelect) next.add(r.id);
        else next.delete(r.id);
      }
      return next;
    });
  };

  const handleAddNote = async (noteText) => {
    if (noteModal.delivery) {
      try {
        await addNote(noteModal.delivery.id, noteText);
      } catch (err) {
        console.error('Failed to add note:', err);
      }
    }
  };

  const handleComplete = async () => {
    if (completeModal.delivery) {
      setIsProcessing(true);
      try {
        await markAsActioned(completeModal.delivery);
        setCompleteModal({ isOpen: false, delivery: null });
      } catch (err) {
        console.error('Failed to mark as actioned:', err);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleSetSoNumber = async (numbers) => {
    if (soNumberModal.delivery) {
      setIsProcessing(true);
      try {
        await submitSoNumber(soNumberModal.delivery, numbers);
        setSoNumberModal({ isOpen: false, delivery: null });
      } catch (err) {
        console.error('Failed to set SO number:', err);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-locate2-bg-light">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Page Title */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-locate2-dark-blue mb-1">Imports</h1>
            <p className="text-locate2-light-gray">Manage and track all import orders</p>
          </div>
          <button
            onClick={() =>
              exportOrdersCsv(
                deliveries.filter((d) => selectedIds.has(d.id)),
                operationType,
              )
            }
            disabled={loading || selectedIds.size === 0}
            className="flex items-center gap-2 px-4 py-2 bg-locate2-primary text-white rounded-lg hover:bg-locate2-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={16} />
            Export {selectedIds.size} selected
          </button>
        </div>

        {/* Operation Type Tabs + Filters */}
        <div className="bg-white rounded-xl border border-locate2-border-light p-4 mb-6">
          <div className="flex flex-col gap-4">
            <FilterTabs
              activeOperation={operationType}
              onOperationChange={handleOperationChange}
              allowedOperations={roleConfig.operations}
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by customer name or order reference..."
              />
              <DateRangeFilter
                value={daysBack}
                onChange={setDaysBack}
                options={DATE_RANGE_OPTIONS}
              />
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <StatsCards counts={statusCounts} />

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-status-error-bg border border-status-error rounded-lg text-status-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Deliveries - Two Sections */}
        {!loading && (
          <div>
            {deliveries.length === 0 ? (
              <div className="bg-white rounded-xl border border-locate2-border-light p-8 text-center">
                <Package size={48} className="mx-auto text-locate2-muted mb-4" />
                <h3 className="text-locate2-dark-blue font-semibold mb-2">No imports found</h3>
                <p className="text-locate2-light-gray text-sm">
                  No {operationType === 'SAP_SO' ? 'Standard' : operationType} orders found. Try a different tab or search term.
                </p>
              </div>
            ) : (
              <>
                {/* Needs Action Section */}
                {needsAction.length > 0 && (
                  <div className="mb-6">
                    <SectionHeader
                      icon={<AlertCircle size={20} className="text-status-error" />}
                      title="Needs Action"
                      rows={needsAction}
                      selectedIds={selectedIds}
                      onToggleAll={(shouldSelect) => handleToggleSection(needsAction, shouldSelect)}
                    />
                    {needsAction.map((delivery) => (
                      <DeliveryCard
                        key={delivery.id}
                        delivery={delivery}
                        operationType={operationType}
                        isExpanded={expandedId === delivery.id}
                        onToggle={() =>
                          setExpandedId(expandedId === delivery.id ? null : delivery.id)
                        }
                        onAddNote={(d) => setNoteModal({ isOpen: true, delivery: d })}
                        onComplete={(d) => setCompleteModal({ isOpen: true, delivery: d })}
                        onSetSoNumber={(d) => setSoNumberModal({ isOpen: true, delivery: d })}
                        onRetry={() => {/* TODO: hook up SAP retry */}}
                        isSelected={selectedIds.has(delivery.id)}
                        onSelectionToggle={handleToggleSelection}
                      />
                    ))}
                  </div>
                )}

                {/* Completed Section */}
                {completed.length > 0 && (
                  <div className="mb-6">
                    <SectionHeader
                      icon={<CheckCircle size={20} className="text-status-success" />}
                      title="Completed"
                      rows={completed}
                      selectedIds={selectedIds}
                      onToggleAll={(shouldSelect) => handleToggleSection(completed, shouldSelect)}
                    />
                    {completed.map((delivery) => (
                      <DeliveryCard
                        key={delivery.id}
                        delivery={delivery}
                        operationType={operationType}
                        isExpanded={expandedId === delivery.id}
                        onToggle={() =>
                          setExpandedId(expandedId === delivery.id ? null : delivery.id)
                        }
                        onAddNote={(d) => setNoteModal({ isOpen: true, delivery: d })}
                        onComplete={(d) => setCompleteModal({ isOpen: true, delivery: d })}
                        onSetSoNumber={(d) => setSoNumberModal({ isOpen: true, delivery: d })}
                        onRetry={() => {/* TODO: hook up SAP retry */}}
                        isSelected={selectedIds.has(delivery.id)}
                        onSelectionToggle={handleToggleSelection}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={noteModal.isOpen}
        onClose={() => setNoteModal({ isOpen: false, delivery: null })}
        onSubmit={handleAddNote}
        deliveryName={noteModal.delivery?.name || ''}
      />

      {/* Complete Confirmation Modal */}
      <CompleteConfirmModal
        isOpen={completeModal.isOpen}
        onClose={() => setCompleteModal({ isOpen: false, delivery: null })}
        onConfirm={handleComplete}
        deliveryName={completeModal.delivery?.name || ''}
        isProcessing={isProcessing}
      />

      {/* Set SO Number Modal */}
      <SetSoNumberModal
        isOpen={soNumberModal.isOpen}
        onClose={() => setSoNumberModal({ isOpen: false, delivery: null })}
        onSubmit={handleSetSoNumber}
        deliveryName={soNumberModal.delivery?.name || ''}
        operationType={soNumberModal.delivery?.operation || ''}
        existingSapSoNumber={soNumberModal.delivery?.sapSoNumber || ''}
        existingPickupSapSoNumber={soNumberModal.delivery?.pickupSapSoNumber || ''}
        isProcessing={isProcessing}
      />
    </div>
  );
}
